import { createSelector } from "reselect";

import type { AppState } from "src/store";
import {
  getDepopIntegrations,
  getEBayIntegrations,
  getEtsyIntegrations,
  getMercariIntegrations,
  getPoshmarkIntegrations,
  getVintedIntegrations,
  getWhatnotIntegrations,
} from "src/store/plaidIntegration/selector";
import { allowedEBayIntegrationsSelector } from "src/store/system/selector";

export type CardPlatform =
  | "Ebay"
  | "Etsy"
  | "Mercari"
  | "Poshmark"
  | "Whatnot"
  | "Vinted"
  | "Depop";

export interface CardPlatformConnection {
  /** The user already has at least one integration for this platform. */
  connected: boolean;
  /** The user has room for another account on this platform. */
  canAddAnother: boolean;
  count: number;
}

/**
 * Connection state for each platform that gets an onboarding setup card.
 *
 * Every platform except eBay is capped at one integration by the backend
 * (`max: 1`), so there is never room for another once one exists. eBay is
 * capped by the per-user `allowed_ebay_integrations` quota instead, so it can
 * be connected and still have room.
 *
 * Keyed on integration counts rather than the integration arrays themselves.
 * Those arrays take a new identity whenever anything in the slice changes — a
 * sync heartbeat writing `salesIsRunning`, say — and `SetupProgress` uses this
 * result as an effect dependency, so recomputing on every sync tick would churn
 * the cards while a sync runs.
 */
export const cardPlatformConnectionsSelector = createSelector(
  [
    (state: AppState) => getEBayIntegrations(state).length,
    (state: AppState) => getEtsyIntegrations(state).length,
    (state: AppState) => getMercariIntegrations(state).length,
    (state: AppState) => getPoshmarkIntegrations(state).length,
    (state: AppState) => getWhatnotIntegrations(state).length,
    (state: AppState) => getVintedIntegrations(state).length,
    (state: AppState) => getDepopIntegrations(state).length,
    allowedEBayIntegrationsSelector,
  ],
  (
    ebay,
    etsy,
    mercari,
    poshmark,
    whatnot,
    vinted,
    depop,
    allowedEBayIntegrations
  ): Record<CardPlatform, CardPlatformConnection> => {
    const capped = (count: number): CardPlatformConnection => ({
      connected: count > 0,
      canAddAnother: count === 0,
      count,
    });

    return {
      Ebay: {
        connected: ebay > 0,
        canAddAnother: ebay < allowedEBayIntegrations,
        count: ebay,
      },
      Etsy: capped(etsy),
      Mercari: capped(mercari),
      Poshmark: capped(poshmark),
      Whatnot: capped(whatnot),
      Vinted: capped(vinted),
      Depop: capped(depop),
    };
  }
);
